import { useCallback, useEffect, useState } from 'react';
import { FiX, FiUsers, FiExternalLink } from 'react-icons/fi';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import { apiRequest } from '../utils/auth';

const BG_CARD = 'var(--color-surface-secondary)';
const BORDER = 'var(--color-border)';
const TEXT = 'var(--color-text)';
const TEXT_DIM = 'var(--color-muted)';
const PURPLE = '#a371f7';
const GREEN = 'var(--color-success)';
const BLUE = 'var(--color-accent)';
const ORANGE = 'var(--color-warning)';
const RED = 'var(--color-danger)';

interface BreakdownEntry { key: string; label: string; count: number }

interface UserStats {
  total: number;
  plans: { free: number; basic: number; pro: number; premium: number; paid: number };
  composition: {
    onboarded: number; creators: number; creatorDisabled: number; internal: number;
    admins: number; referred: number; referrers: number; paypalLinked: number;
    notificationsOn: number; pendingPlanChange: number; billingIssue: number;
  };
  activity: {
    newLast7Days: number; newLast30Days: number;
    active1Day: number; active7Days: number; active30Days: number;
    dormant: number; neverActive: number;
  };
  byPlan: BreakdownEntry[];
  byAuthProvider: BreakdownEntry[];
  byPlatform: BreakdownEntry[];
  byLanguage: BreakdownEntry[];
  byAppVersion: BreakdownEntry[];
  bySubscribePlatform: BreakdownEntry[];
}

interface UserStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Opens the Users page, so the modal can hand off to the full list. */
  onViewAll?: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px',
};

const modalStyle: React.CSSProperties = {
  backgroundColor: BG_CARD,
  border: `1px solid ${BORDER}`,
  borderRadius: '12px',
  width: '100%',
  maxWidth: '880px',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

function Total({ label, value, color, hint }: {
  label: string; value: number; color?: string; hint?: string;
}) {
  return (
    <div style={{ background: 'var(--color-bg)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ color: TEXT_DIM, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ color: color || TEXT, fontSize: 20, fontWeight: 700, marginTop: 2 }}>
        {value.toLocaleString()}
      </div>
      {hint && <div style={{ color: TEXT_DIM, fontSize: 11 }}>{hint}</div>}
    </div>
  );
}

/**
 * A distribution as labelled bars. Shares are of `total` (every user document),
 * except where the axis covers a subset — paying subscribers only, for the
 * payment channel — and the share would read against the wrong denominator.
 */
function Distribution({ title, rows, total, showShare, note, color }: {
  title: string;
  rows: BreakdownEntry[];
  total: number;
  showShare: boolean;
  note?: string;
  color?: string;
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  return (
    <div style={{ background: 'var(--color-bg)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ color: TEXT, fontSize: 13, fontWeight: 600, marginBottom: note ? 2 : 10 }}>{title}</div>
      {note && <div style={{ color: TEXT_DIM, fontSize: 11, marginBottom: 10 }}>{note}</div>}
      {rows.length === 0 ? (
        <div style={{ color: TEXT_DIM, fontSize: 12 }}>No users yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((row) => (
            <div key={row.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, marginBottom: 3 }}>
                <span style={{ color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.label}>
                  {row.label}
                </span>
                <span style={{ color: TEXT_DIM, whiteSpace: 'nowrap' }}>
                  {row.count.toLocaleString()}
                  {showShare && total > 0 && ` · ${((row.count / total) * 100).toFixed(1)}%`}
                </span>
              </div>
              <div style={{ height: 6, background: 'var(--color-surface-hover)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: max > 0 ? `${(row.count / max) * 100}%` : '0%',
                  height: '100%',
                  background: color || GREEN,
                  borderRadius: 3,
                }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UserStatsModal({ isOpen, onClose, onViewAll }: UserStatsModalProps) {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // apiRequest spreads the { success, data } envelope, so the payload's
      // fields arrive at the top level
      const response = await apiRequest<UserStats & { success: boolean }>('/api/admin/user-stats');
      setStats(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchStats();
  }, [isOpen, fetchStats]);

  // Escape dismisses the modal, same as the × button and the backdrop click.
  useEscapeToClose(isOpen, onClose);

  if (!isOpen) return null;

  const share = (value: number) =>
    stats && stats.total > 0 ? `${((value / stats.total) * 100).toFixed(1)}% of all` : undefined;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiUsers style={{ color: GREEN, fontSize: 18 }} />
            <div>
              <h2 style={{ margin: 0, color: TEXT, fontSize: 17 }}>User Statistics</h2>
              <div style={{ color: TEXT_DIM, fontSize: 12 }}>
                {stats ? `${stats.total.toLocaleString()} users` : 'All users'} · every account, internal ones included
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onViewAll && (
              <button
                type="button"
                onClick={onViewAll}
                style={{
                  background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6,
                  color: TEXT, fontSize: 12, padding: '6px 10px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                Users page <FiExternalLink />
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              style={{ background: 'none', border: 'none', color: TEXT_DIM, fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
            >
              <FiX />
            </button>
          </div>
        </div>

        <div style={{ padding: '18px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: TEXT_DIM }}>Loading…</div>
          ) : error ? (
            <div style={{ color: RED, background: 'var(--color-surface)', border: `1px solid ${RED}`, borderRadius: 8, padding: '14px' }}>
              {error}
            </div>
          ) : stats ? (
            <>
              <div>
                <div style={{ color: TEXT, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Plan mix</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                  <Total label="Total" value={stats.total} />
                  <Total label="Free" value={stats.plans.free} color={TEXT_DIM} hint={share(stats.plans.free)} />
                  <Total label="Basic" value={stats.plans.basic} color={BLUE} />
                  <Total label="Pro" value={stats.plans.pro} color={PURPLE} />
                  <Total label="Premium" value={stats.plans.premium} color={ORANGE} />
                  <Total label="Paid total" value={stats.plans.paid} color={GREEN} hint={share(stats.plans.paid)} />
                </div>
              </div>

              <div>
                <div style={{ color: TEXT, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Account composition</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                  <Total label="Onboarded" value={stats.composition.onboarded} color={GREEN} hint="picked a FRAW ID" />
                  <Total label="Creators" value={stats.composition.creators} color={PURPLE} hint="can publish public lenses" />
                  <Total label="Creator disabled" value={stats.composition.creatorDisabled} color={RED} hint="policy violation" />
                  <Total label="Notifications on" value={stats.composition.notificationsOn} color={BLUE} />
                  <Total label="Referred" value={stats.composition.referred} color={BLUE} hint="signed up via a code" />
                  <Total label="Referrers" value={stats.composition.referrers} color={BLUE} hint="referred someone" />
                  <Total label="PayPal linked" value={stats.composition.paypalLinked} color={GREEN} hint="can be paid out" />
                  <Total label="Pending plan change" value={stats.composition.pendingPlanChange} color={ORANGE} hint="deferred downgrade" />
                  <Total label="Billing issue" value={stats.composition.billingIssue} color={RED} hint="payment outstanding" />
                  <Total label="Internal" value={stats.composition.internal} color={TEXT_DIM} hint="excluded from growth stats" />
                  <Total label="Admins" value={stats.composition.admins} color={TEXT_DIM} />
                </div>
              </div>

              <div>
                <div style={{ color: TEXT, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Signups & recency</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                  <Total label="New (7d)" value={stats.activity.newLast7Days} color={BLUE} />
                  <Total label="New (30d)" value={stats.activity.newLast30Days} color={BLUE} />
                  <Total label="Active (1d)" value={stats.activity.active1Day} color={GREEN} />
                  <Total label="Active (7d)" value={stats.activity.active7Days} color={GREEN} />
                  <Total label="Active (30d)" value={stats.activity.active30Days} color={GREEN} hint={share(stats.activity.active30Days)} />
                  <Total label="Dormant" value={stats.activity.dormant} color={ORANGE} hint="last launch over 30d ago" />
                  <Total label="Never active" value={stats.activity.neverActive} color={TEXT_DIM} hint="no signed-in app launch" />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12 }}>
                <Distribution
                  title="By plan"
                  rows={stats.byPlan}
                  total={stats.total}
                  showShare
                  color={GREEN}
                />
                <Distribution
                  title="By auth provider"
                  rows={stats.byAuthProvider}
                  total={stats.total}
                  showShare
                  color={BLUE}
                />
                <Distribution
                  title="By device platform"
                  rows={stats.byPlatform}
                  total={stats.total}
                  showShare
                  note="From each user's most recent app launch."
                  color={PURPLE}
                />
                <Distribution
                  title="By app language"
                  rows={stats.byLanguage}
                  total={stats.total}
                  showShare
                  color={BLUE}
                />
                <Distribution
                  title="By app version"
                  rows={stats.byAppVersion}
                  total={stats.total}
                  showShare
                  note="From each user's most recent app launch."
                  color={ORANGE}
                />
                <Distribution
                  title="By payment channel"
                  rows={stats.bySubscribePlatform}
                  total={stats.plans.paid}
                  showShare={false}
                  note={`Paying subscribers only (${stats.plans.paid.toLocaleString()}), by where they bought.`}
                  color={GREEN}
                />
              </div>

              <div style={{ color: TEXT_DIM, fontSize: 11, lineHeight: 1.6, borderTop: `1px solid ${BORDER}`, paddingTop: 12 }}>
                Counts cover every user document — internal and admin accounts included — so they add up
                to the Total Users figure on the dashboard. The DAU/WAU/MAU cards further down the
                dashboard deliberately exclude internal accounts, so they read slightly lower than the
                Active figures here. Legacy <code>tier1</code>/<code>tier2</code> plans are folded into
                Basic and Pro. Long tails are rolled into an “Others” row.
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
